<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Marketplace Order Item"
                sample-csv-path="/createMarketplaceOrderItemByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Create Marketplace Order Item by Import </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListMarketplaceOrderItem"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { MarketplaceOrderItemLabelTextHelper } from '../../utils/labelTextHelper/MarketplaceOrderItemLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { MarketplaceOrderItemHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderItemHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { MarketplaceOrderItemImportValidation } from '../../config/importValidationConfigs';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';
export default defineComponent({
    name: 'ImportMarketplaceOrderItem',

    components: {
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            mappedCsv: null,
            fields: MarketplaceOrderItemHeaderHelper.getImportFields(),
            previewHeaders: MarketplaceOrderItemHeaderHelper.getImportHeaders(),
            headers: [...ImportHeaderHelper.getHeaders(), ...MarketplaceOrderItemHeaderHelper.getImportHeaders()]
        });
        return {
            data
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListMarketplaceOrderItem: PathDefinition.listMarketplaceOrder,
            formLabelText: MarketplaceOrderItemLabelTextHelper.getLabelText
        };
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            return ImportValidationHelper.validateRows(
                mappedCsv,
                MarketplaceOrderItemImportValidation,
                importHeaderValues
            );
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;
            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                for (let i = 0; i < this.data.mappedCsv.length; i++) {
                    await this.create(i);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, MarketplaceOrderItemImportValidation);
        },
        async create(idx) {
            const csvData = this.data.mappedCsv[idx];
            const response = await this.createMarketplaceOrderItem(csvData);
            this.processResponse(response, idx, csvData);
        },
        async createMarketplaceOrderItem(csvData) {
            const requestFields = MarketplaceOrderItemHeaderHelper.getImportHeaders().map((header) => header.value);
            const request = _.pick(csvData, requestFields);
            const requestUrl = `/api/staff/marketplaceOrderItem/createByKey`;
            return await fetchWrapper.post(requestUrl, request);
        },
        processResponse(response, idx, csvData) {
            if (response.success) {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
            } else {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = response.errors;
            }
            this.data.mappedCsv.splice(idx, 1, csvData);
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
